/*
  "Resume file": one export carrying every characteristic measured for a well.

  Symbiomics (Bruna Caetano, MIRA Platform Requirements, 2026-07-29) asked for a
  single download per well instead of the per-characteristic XLSX buttons that
  exist today, so the raw numbers behind every graph can be processed
  independently. The shape below follows her Figures 5 and 6 exactly:

    Experiment I  (WITH barrier, two colonies)
        Plate1_A1_Component1.xlsx      <- left side
          Colony 1 color | Colony 1 area | Left media color
          Colony 1 border | Left media area
        Plate1_A1_Component2.xlsx      <- right side
          Colony 2 color | Colony 2 area | Right media color
          Colony 2 border | Right media area

    Experiment II (WITHOUT barrier, one colony)
        Plate1_A1.xlsx
          Colony color | Colony area | Media color | Colony border | Media area

  Experiment I is the barrier plate -- the 22 May run and the 29 July "Barrier
  Plate" run. Experiment II is the single-colony plate -- the 12 June merged run.
  (The requirements PDF's prose has the two the other way round; the DIAGRAM is
  correct and is what this follows.)

  TWO SHEETS PER SIDE COME FROM THE MEDIUM, and the medium is a SEPARATE
  analysis from the colony (they are segmented independently and, in the Jynx
  data, do not even share frame indices). So the caller passes colony and media
  sources separately; a missing medium yields an empty sheet with its headers
  rather than a missing sheet, because a reader opening the file needs to see
  that the column existed and was not measured.

  Everything here is pure: it builds {filename, sheets:[{name, rows}]} and never
  touches XLSX or the DOM, so the layout is unit-testable without a browser.
*/

/** Experiment types. The barrier case yields two files, one per side. */
export const EXPERIMENT_I = 'experiment_1';    // WITH barrier, a colony either side
export const EXPERIMENT_II = 'experiment_2';   // WITHOUT barrier, single colony

/**
 * Infer the experiment type when it was not recorded at setup.
 *
 * Until experiment-type selection ships (Symbiomics request 3), stored
 * analyses carry no such field, so the count of colony objects is the only
 * signal available: two colonies in one well means a barrier plate. This is a
 * FALLBACK — once the setup step records the type, pass it in explicitly and
 * never rely on this.
 */
export function inferExperimentType({ declaredType, colonyCount } = {}) {
  if (declaredType === EXPERIMENT_I || declaredType === EXPERIMENT_II) return declaredType;
  return Number(colonyCount) >= 2 ? EXPERIMENT_I : EXPERIMENT_II;
}

/** Sheet titles for a side. `side` is null (Experiment II, no barrier), 'left' or 'right'. */
export function sheetNames(side, colonyIndex) {
  if (!side) {
    return {
      colonyColor: 'Colony color',
      colonyArea: 'Colony area',
      mediaColor: 'Media color',
      colonyBorder: 'Colony border',
      mediaArea: 'Media area',
    };
  }
  const m = side === 'left' ? 'Left media' : 'Right media';
  return {
    colonyColor: `Colony ${colonyIndex} color`,
    colonyArea: `Colony ${colonyIndex} area`,
    mediaColor: `${m} color`,
    colonyBorder: `Colony ${colonyIndex} border`,
    mediaArea: `${m} area`,
  };
}

// Column headers. Time is carried on every sheet: the whole point of the export
// is downstream processing, and a characteristic without its time axis cannot be
// replotted or joined to anything.
const T = ['Frame', 'Time_Elapsed_h', 'Timestamp_UTC'];
export const HEADERS = {
  color: [...T, 'Hue', 'Saturation', 'Value', 'Hue_SD', 'Saturation_SD', 'Value_SD', 'Pixel_Count'],
  // E (left) and J (right) are the media extent: the diagram draws them as
  // spans, and Symbiomics treats area and diameter as interchangeable readings
  // of the same thing, so both ship rather than forcing a choice.
  area: [...T, 'Area_px2', 'Growth_Rate', 'Equivalent_Diameter_px'],
  border: [...T, 'Perimeter_px', 'Circularity', 'Irregularity', 'Convexity', 'Solidity', 'Eccentricity'],
};

const num = (v) => (v === null || v === undefined || v === '' ? '' : v);

/**
 * Build the rows for one characteristic.
 * `frames` is [{frame_idx, ...}], `time` resolves a frame index to
 * {elapsedH, iso}. Rows come out ordered by frame so the sheet reads as a time
 * series without the reader having to sort it.
 */
export function buildRows(kind, frames, time) {
  const header = HEADERS[kind];
  if (!Array.isArray(frames) || frames.length === 0) return [header];
  const t = time || (() => ({ elapsedH: '', iso: '' }));
  const sorted = [...frames].sort((a, b) => (a.frame_idx ?? 0) - (b.frame_idx ?? 0));
  return [header, ...sorted.map((f) => {
    const { elapsedH, iso } = t(f.frame_idx);
    const lead = [f.frame_idx ?? '', elapsedH, iso];
    if (kind === 'color') {
      return [...lead, num(f.hue), num(f.saturation), num(f.value),
        num(f.hue_std), num(f.saturation_std), num(f.value_std), num(f.pixel_count)];
    }
    if (kind === 'area') {
      return [...lead, num(f.area_pixels ?? f.area_px), num(f.growth_rate),
        num(f.equivalent_diameter_px)];
    }
    return [...lead, num(f.perimeter_px), num(f.circularity), num(f.irregularity),
      num(f.convexity), num(f.solidity), num(f.eccentricity)];
  })];
}

/**
 * One workbook for one side of a well.
 *
 * `colony` and `media` are {color:[frames], area:[frames], border:[frames]}.
 * Border is colony-only by design: an outline metric on the surrounding medium
 * describes the shape of whatever the colony has not yet covered, which is not
 * a property of the medium and would read as one.
 */
export function buildWorkbook({ plate, well, side, colonyIndex, colony, media, time }) {
  const n = sheetNames(side, colonyIndex);
  const c = colony || {};
  const m = media || {};
  const suffix = side ? `_Component${colonyIndex}` : '';
  return {
    filename: `Plate${plate}_${well}${suffix}.xlsx`,
    sheets: [
      { name: n.colonyColor, rows: buildRows('color', c.color, time) },
      { name: n.colonyArea, rows: buildRows('area', c.area, time) },
      { name: n.mediaColor, rows: buildRows('color', m.color, time) },
      { name: n.colonyBorder, rows: buildRows('border', c.border, time) },
      { name: n.mediaArea, rows: buildRows('area', m.area, time) },
    ],
  };
}

/**
 * The export for a whole well: TWO workbooks for Experiment I (barrier;
 * left = Component 1, right = Component 2) and ONE for Experiment II.
 *
 * `components` is [{side, colonyIndex, colony, media}] — the caller decides
 * which analysis is the left and which the right, because only it knows how the
 * analyses were named and segmented.
 */
export function buildWellExport({ plate, well, experimentType, components, time }) {
  const type = experimentType || EXPERIMENT_II;      // single colony is the simpler default
  if (type === EXPERIMENT_II) {
    const c = (components && components[0]) || {};
    return [buildWorkbook({
      plate, well, side: null, colonyIndex: 1,
      colony: c.colony, media: c.media, time,
    })];
  }
  return (components || []).map((c, i) => buildWorkbook({
    plate, well,
    side: c.side || (i === 0 ? 'left' : 'right'),
    colonyIndex: c.colonyIndex || i + 1,
    colony: c.colony, media: c.media, time,
  }));
}
